import React from 'react';
import { useForm, Field, FieldProps, FieldRenderProps } from 'react-final-form';
import AutoSave from '../AutoSave';
import MultiSelectInput, {
  OptionItem,
} from '../../../components/Inputs/MultiSelect';
import { IndicatorStateOptions } from '../../../components/Indicator';

// FIXME making custom props optional until this is resolved:
// https://github.com/final-form/react-final-form/issues/175
type MultiChoiceInputMetaProps = {
  invalid?: boolean;
  error?: boolean;
  initial?: any;
  disabled?: boolean;
  submitting?: boolean;
  submitFailed?: boolean;
  submitSucceeded?: boolean;
  dirtySinceLastSubmit?: boolean;
  dirty?: boolean;
};

const getIndicatorState = (
  props: MultiChoiceInputMetaProps,
): IndicatorStateOptions => {
  let state: IndicatorStateOptions = 'new';
  if (props.initial) state = 'complete';
  if (props.error || props.invalid) state = 'error';
  if (props.submitting && (props.dirtySinceLastSubmit || props.dirty))
    state = 'loading';
  return state;
};

interface MultiChoiceInputFieldComponentProps
  extends FieldRenderProps<string, HTMLInputElement> {
  text?: string;
  informationText?: string[];
  enabled?: boolean;
  supportingLink?: React.MouseEventHandler;
  handleSubmit?: () => void;
  options?: OptionItem[];
}
const isEqual = (a: any, b: any) => {
  if (a === b) {
    return true;
  }

  if (!a && !b) {
    return true;
  }

  if (Array.isArray(a) && Array.isArray(b) && a.length === b.length) {
    return a.reduce((agg, curr, i) => agg && curr === b[i], true);
  }

  if (Array.isArray(a) && a.length === 1) return a[0] === b;
  if (Array.isArray(b) && b.length === 1) return a === b[0];

  return false;
};

const MultiChoiceInputFieldComponent = ({
  input,
  meta,
  handleSubmit,
  enabled,
  ...rest
}: MultiChoiceInputFieldComponentProps) => {
  return (
    <AutoSave
      name={input.name}
      isEqual={isEqual}
      handleSubmit={handleSubmit ? handleSubmit : () => {}}>
      <MultiSelectInput
        {...input}
        {...meta}
        enabled={enabled}
        // disabled={meta.submitting}
        indicatorState={getIndicatorState(meta)}
        {...rest}
      />
    </AutoSave>
  );
};

export interface MultiChoiceInputFieldProps
  extends FieldProps<string, FieldRenderProps<string>> {
  text?: string;
  informationText?: string[];
  supportingLink?: React.MouseEventHandler;
  options?: OptionItem[];
  enabled?: boolean;
}

const MultiChoiceField = (props: MultiChoiceInputFieldProps) => {
  const form = useForm();
  const required = !(props.enabled === false) && props.required;
  return (
    <Field<string>
      isEqual={isEqual}
      component={MultiChoiceInputFieldComponent}
      handleSubmit={form.submit}
      {...props}
      required={required}
      validate={required ? props.validate : undefined}
    />
  );
};

export default MultiChoiceField;
